import DOMPurify from "dompurify";
import {
  ResourceEntityInput,
  ResourceFileDto,
} from "../../../services/resources/resources.types";
import { ResourceType, type ReadingVariant } from "../../../types";
import type {
  CreateReadingBlockDto,
  LocalBlock,
} from "../../../services/reading-content/reading-content.types";
import { SANITIZE_CONFIG } from "../../ReadingContentEditor/sanitizeConfig";

export interface PdfFileItem {
  id: string;
  file_id: string;
  url: string | null;
  uploading: boolean;
  progress?: number;
  dbId?: number; // Database ID for existing files (used in PATCH operations)
}

/**
 * Transforma campos legacy a formato nuevo files[] con file_type según el tipo de recurso
 */
export const buildFilesArray = (
  resourceType: ResourceType,
  file_id: string,
  extra_file_id: string,
  additional_extra_file_id: string,
  student_book_file_id: string,
  extra_label: string,
  additional_label: string,
  pdfFiles?: PdfFileItem[],
  picture_card_file_id?: string,
  file_id_ios?: string,
  readingVariant?: ReadingVariant | null,
  diagnostic_teacher_file_id?: string,
  diagnostic_student_file_id?: string,
  diagnostic_student_editable_file_id?: string,
): ResourceFileDto[] => {
  const files: ResourceFileDto[] = [];

  // Handle READING kinder: treat file as MAIN_FILE (PDF upload)
  if (resourceType === ResourceType.READING && readingVariant === "kinder") {
    if (file_id) {
      files.push({ file_id, file_type: "MAIN_FILE" });
    }
    return files;
  }

  // DIAGNOSTIC carries two PDFs, one per audience, plus an optional Word
  // copy of the student version
  if (resourceType === ResourceType.DIAGNOSTIC) {
    if (diagnostic_student_file_id) {
      files.push({
        file_id: diagnostic_student_file_id,
        file_type: "DIAGNOSTIC_STUDENT",
      });
    }
    if (diagnostic_teacher_file_id) {
      files.push({
        file_id: diagnostic_teacher_file_id,
        file_type: "DIAGNOSTIC_TEACHER",
      });
    }
    if (diagnostic_student_editable_file_id) {
      files.push({
        file_id: diagnostic_student_editable_file_id,
        file_type: "DIAGNOSTIC_STUDENT_EDITABLE",
      });
    }
    return files;
  }

  // Handle READING primaria: no files needed (content is in blocks)
  if (resourceType === ResourceType.READING && readingVariant === "primaria") {
    return files;
  }

  // Handle DOWNLOAD type with multiple PDFs
  if (resourceType === ResourceType.DOWNLOAD && pdfFiles) {
    pdfFiles.forEach((pdf, index) => {
      if (pdf.file_id) {
        files.push({
          file_id: pdf.file_id,
          file_type: "MAIN_FILE",
          label: `PDF ${index + 1}`,
        });
      }
    });
    // picture card goes after other pdfs
    if (picture_card_file_id) {
      files.push({ file_id: picture_card_file_id, file_type: "PICTURE_CARD" });
    }
    return files;
  }

  if (file_id) {
    let fileType: ResourceFileDto["file_type"] = "MAIN_FILE";

    if (resourceType === ResourceType.BOOK) {
      fileType = "TEACHER_BOOK";
    } else if (resourceType === ResourceType.GAME_QUIZ) {
      fileType = "SUPPLEMENTARY";
    } else if (resourceType === ResourceType.AUDIO) {
      fileType = "AUDIO_TRACK";
    }

    files.push({ file_id, file_type: fileType });
  }

  // Student book (solo para BOOK)
  if (student_book_file_id && resourceType === ResourceType.BOOK) {
    files.push({
      file_id: student_book_file_id,
      file_type: "STUDENT_BOOK",
    });
  }

  // Extra file (depende del tipo de recurso)
  if (extra_file_id) {
    let fileType: ResourceFileDto["file_type"] = "MAIN_FILE";

    if (resourceType === ResourceType.AUDIO) {
      fileType = "TRANSCRIPT";
    } else if (resourceType === ResourceType.BOOK) {
      fileType = "AUDIO_TRACK";
    }

    files.push({
      file_id: extra_file_id,
      file_type: fileType,
      ...(extra_label ? { label: extra_label } : {}),
    });
  }

  // Additional extra file (solo para BOOK como AUDIO_TRACK)
  if (additional_extra_file_id && resourceType === ResourceType.BOOK) {
    files.push({
      file_id: additional_extra_file_id,
      file_type: "AUDIO_TRACK",
      ...(additional_label ? { label: additional_label } : {}),
    });
  }

  // Versión iOS del juego (solo para GAME)
  if (file_id_ios && resourceType === ResourceType.GAME) {
    files.push({
      file_id: file_id_ios,
      file_type: "MAIN_FILE_IOS",
    });
  }

  return files;
};

/**
 * Construye el array de files para operaciones de UPDATE (PATCH)
 * Incluye los IDs de archivos existentes para mantener las referencias
 */
export const buildFilesArrayForUpdate = (
  resourceType: ResourceType,
  file_id: string,
  extra_file_id: string,
  additional_extra_file_id: string,
  student_book_file_id: string,
  extra_label: string,
  additional_label: string,
  existingFileIds: {
    file_id_db_id?: number;
    file_id_ios_db_id?: number;
    student_book_db_id?: number;
    extra_file_db_id?: number;
    additional_extra_file_db_id?: number;
    picture_card_db_id?: number;
    diagnostic_teacher_db_id?: number;
    diagnostic_student_db_id?: number;
    diagnostic_student_editable_db_id?: number;
  },
  pdfFiles?: PdfFileItem[],
  picture_card_file_id?: string,
  file_id_ios?: string,
  readingVariant?: ReadingVariant | null,
  diagnostic_teacher_file_id?: string,
  diagnostic_student_file_id?: string,
  diagnostic_student_editable_file_id?: string,
): ResourceFileDto[] => {
  const files: ResourceFileDto[] = [];

  // DIAGNOSTIC carries two PDFs, one per audience, plus an optional Word
  // copy of the student version
  if (resourceType === ResourceType.DIAGNOSTIC) {
    if (diagnostic_student_file_id) {
      const studentDto: ResourceFileDto = {
        file_id: diagnostic_student_file_id,
        file_type: "DIAGNOSTIC_STUDENT",
      };
      if (existingFileIds.diagnostic_student_db_id) {
        studentDto.id = existingFileIds.diagnostic_student_db_id;
      }
      files.push(studentDto);
    }
    if (diagnostic_teacher_file_id) {
      const teacherDto: ResourceFileDto = {
        file_id: diagnostic_teacher_file_id,
        file_type: "DIAGNOSTIC_TEACHER",
      };
      if (existingFileIds.diagnostic_teacher_db_id) {
        teacherDto.id = existingFileIds.diagnostic_teacher_db_id;
      }
      files.push(teacherDto);
    }
    if (diagnostic_student_editable_file_id) {
      const editableDto: ResourceFileDto = {
        file_id: diagnostic_student_editable_file_id,
        file_type: "DIAGNOSTIC_STUDENT_EDITABLE",
      };
      if (existingFileIds.diagnostic_student_editable_db_id) {
        editableDto.id = existingFileIds.diagnostic_student_editable_db_id;
      }
      files.push(editableDto);
    }
    return files;
  }

  // Handle READING kinder: treat file as MAIN_FILE (PDF upload)
  if (resourceType === ResourceType.READING && readingVariant === "kinder") {
    if (file_id) {
      const fileDto: ResourceFileDto = { file_id, file_type: "MAIN_FILE" };
      if (existingFileIds.file_id_db_id) {
        fileDto.id = existingFileIds.file_id_db_id;
      }
      files.push(fileDto);
    }
    return files;
  }

  // Handle READING primaria: no files needed (content is in blocks)
  if (resourceType === ResourceType.READING && readingVariant === "primaria") {
    return files;
  }

  // Handle DOWNLOAD type with multiple PDFs
  if (resourceType === ResourceType.DOWNLOAD && pdfFiles) {
    pdfFiles.forEach((pdf, index) => {
      if (pdf.file_id) {
        const fileDto: ResourceFileDto = {
          file_id: pdf.file_id,
          file_type: "MAIN_FILE",
          label: `PDF ${index + 1}`,
        };
        // Include ID if this is an existing file
        if (pdf.dbId) {
          fileDto.id = pdf.dbId;
        }
        files.push(fileDto);
      }
    });
    // picture card after pdfs
    if (picture_card_file_id) {
      const picDto: ResourceFileDto = {
        file_id: picture_card_file_id,
        file_type: "PICTURE_CARD",
      };
      if (existingFileIds.picture_card_db_id) {
        picDto.id = existingFileIds.picture_card_db_id;
      }
      files.push(picDto);
    }
    return files;
  }

  if (file_id) {
    let fileType: ResourceFileDto["file_type"] = "MAIN_FILE";

    if (resourceType === ResourceType.BOOK) {
      fileType = "TEACHER_BOOK";
    } else if (resourceType === ResourceType.GAME_QUIZ) {
      fileType = "SUPPLEMENTARY";
    } else if (resourceType === ResourceType.AUDIO) {
      fileType = "AUDIO_TRACK";
    }

    const fileDto: ResourceFileDto = { file_id, file_type: fileType };
    // Include existing ID if present
    if (existingFileIds.file_id_db_id) {
      fileDto.id = existingFileIds.file_id_db_id;
    }
    files.push(fileDto);
  }

  // Student book (solo para BOOK)
  if (student_book_file_id && resourceType === ResourceType.BOOK) {
    const fileDto: ResourceFileDto = {
      file_id: student_book_file_id,
      file_type: "STUDENT_BOOK",
    };
    if (existingFileIds.student_book_db_id) {
      fileDto.id = existingFileIds.student_book_db_id;
    }
    files.push(fileDto);
  }

  // Extra file (depende del tipo de recurso)
  if (extra_file_id) {
    let fileType: ResourceFileDto["file_type"] = "MAIN_FILE";

    if (resourceType === ResourceType.AUDIO) {
      fileType = "TRANSCRIPT";
    } else if (resourceType === ResourceType.BOOK) {
      fileType = "AUDIO_TRACK";
    }

    const fileDto: ResourceFileDto = {
      file_id: extra_file_id,
      file_type: fileType,
      ...(extra_label ? { label: extra_label } : {}),
    };
    if (existingFileIds.extra_file_db_id) {
      fileDto.id = existingFileIds.extra_file_db_id;
    }
    files.push(fileDto);
  }

  // Additional extra file (solo para BOOK como AUDIO_TRACK)
  if (additional_extra_file_id && resourceType === ResourceType.BOOK) {
    const fileDto: ResourceFileDto = {
      file_id: additional_extra_file_id,
      file_type: "AUDIO_TRACK",
      ...(additional_label ? { label: additional_label } : {}),
    };
    if (existingFileIds.additional_extra_file_db_id) {
      fileDto.id = existingFileIds.additional_extra_file_db_id;
    }
    files.push(fileDto);
  }

  // Versión iOS del juego (solo para GAME)
  if (file_id_ios && resourceType === ResourceType.GAME) {
    const fileDto: ResourceFileDto = {
      file_id: file_id_ios,
      file_type: "MAIN_FILE_IOS",
    };
    if (existingFileIds.file_id_ios_db_id) {
      fileDto.id = existingFileIds.file_id_ios_db_id;
    }
    files.push(fileDto);
  }

  return files;
};

/**
 * Builds the create/update payload for a single reading block on the save
 * path: sanitizes pasted HTML with the narrowed WU3 config (WU10, SCRUM-605)
 * and carries section_title/image_position through (fields captured on load
 * per WU4 but previously dropped from create/updateBlock/addBlock request
 * bodies). Shared by `saveReadingContent`'s create/update/add branches so
 * every write path stays byte-for-byte in sync.
 */
export const buildReadingBlockDto = (
  block: Pick<
    LocalBlock,
    "type" | "content" | "imageUrl" | "sectionTitle" | "imagePosition"
  >,
  orderBy: number,
  pageNumber: number,
): CreateReadingBlockDto => ({
  order_by: orderBy,
  type: block.type,
  content: block.content
    ? DOMPurify.sanitize(block.content, SANITIZE_CONFIG)
    : undefined,
  image_url: block.imageUrl || undefined,
  image_position: block.imagePosition || undefined,
  section_title: block.sectionTitle || undefined,
  page_number: pageNumber,
});

export const initialValuesResource: ResourceEntityInput = {
  title: "",
  additional_info: "",
  file_id: "",
  file_id_ios: "",
  extra_file_id: "",
  additional_extra_file_id: "",
  student_book_file_id: "",
  diagnostic_teacher_file_id: "",
  diagnostic_student_file_id: "",
  picture_card_file_id: "",
  additional_extra_file_id_related_field: "",
  extra_file_id_related_field: "",
  type: ResourceType.VIDEO,
  is_secondary: false,
  cover: "",
  genially_edit_url: "",
  script_doc_url: "",
  drive_url: "",
};
